/**
 * Typesense Search Configuration
 * 
 * Typesense is an open-source search engine optimized for instant search experiences.
 * This utility uses native fetch API instead of the Typesense client library to avoid
 * webpack bundling issues with axios in React.
 * 
 * Setup Required:
 * 1. Install Typesense on VPS: https://typesense.org/docs/guide/install-typesense.html
 * 2. Set environment variables in .env:
 *    REACT_APP_TYPESENSE_HOST=your-vps-ip
 *    REACT_APP_TYPESENSE_PORT=8108
 *    REACT_APP_TYPESENSE_PROTOCOL=http (or https if SSL configured)
 *    REACT_APP_TYPESENSE_API_KEY=your-search-only-api-key
 * 3. Run sync script to populate initial data: node scripts/syncTypesense.js
 */

// Typesense configuration
const getTypesenseConfig = () => {
  const apiKey = process.env.REACT_APP_TYPESENSE_API_KEY;

  // REACT_APP_TYPESENSE_BASE_URL takes priority — use this when Typesense is proxied
  // through nginx (HTTPS) to avoid mixed-content blocks from the browser.
  // e.g. https://api.itexpertsllc.com/typesense
  const baseUrl =
    process.env.REACT_APP_TYPESENSE_BASE_URL ||
    (() => {
      const host = process.env.REACT_APP_TYPESENSE_HOST;
      const port = process.env.REACT_APP_TYPESENSE_PORT || '8108';
      const protocol = process.env.REACT_APP_TYPESENSE_PROTOCOL || 'http';
      if (!host) return null;
      return `${protocol}://${host}:${port}`;
    })();

  if (!baseUrl || !apiKey) {
    console.warn('[Typesense] Missing configuration. Searches will fall back to Firestore.');
    return null;
  }

  return { baseUrl, apiKey };
};

// Helper: Make Typesense API request using native fetch
const typesenseRequest = async (endpoint, method = 'GET', body = null) => {
  const config = getTypesenseConfig();
  if (!config) throw new Error('Typesense not configured');

  const url = `${config.baseUrl}${endpoint}`;
  const options = {
    method,
    headers: {
      'X-TYPESENSE-API-KEY': config.apiKey,
      'Content-Type': 'application/json',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(error.message || `Typesense error: ${response.status}`);
  }

  return response.json();
};

// Check if Typesense is available
export const isTypesenseAvailable = async () => {
  try {
    const config = getTypesenseConfig();
    if (!config) return false;
    
    await typesenseRequest('/health');
    return true;
  } catch (error) {
    console.error('[Typesense] Health check failed:', error.message);
    return false;
  }
};

/**
 * Collection Schemas
 * Define searchable fields, sortable fields, and faceting capabilities
 */

export const COLLECTION_SCHEMAS = {
  customers: {
    name: 'customers',
    fields: [
      { name: 'id', type: 'string', facet: false },
      { name: 'customerName', type: 'string', facet: false },
      { name: 'customerEmail', type: 'string', facet: false, optional: true },
      { name: 'customerPhone', type: 'string', facet: false, optional: true },
      { name: 'customerStatus', type: 'string', facet: true, optional: true },
      { name: 'businessFocus', type: 'string[]', facet: true, optional: true },
      { name: 'apparelMagicId', type: 'string', facet: false, optional: true },
      { name: 'salesRepNames', type: 'string[]', facet: true, optional: true },
      { name: 'city', type: 'string', facet: true, optional: true },
      { name: 'state', type: 'string', facet: true, optional: true },
      { name: 'createdAt', type: 'int64', facet: false, optional: true },
      { name: 'updatedAt', type: 'int64', facet: false, optional: true },
    ],
    default_sorting_field: 'customerName',
  },

  orders: {
    name: 'orders',
    fields: [
      { name: 'id', type: 'string', facet: false }, // Document ID (not searchable)
      { name: 'order_number', type: 'string', facet: false }, // Searchable order number
      { name: 'customer_id', type: 'string', facet: false, optional: true },
      { name: 'customer_name', type: 'string', facet: false, optional: true },
      { name: 'amount', type: 'float', facet: false, optional: true },
      { name: 'status', type: 'string', facet: true, optional: true },
      { name: 'date_internal', type: 'string', facet: false, optional: true }, // YYYY-MM-DD
      { name: 'creation_time', type: 'int64', facet: false, optional: true }, // Unix timestamp
      { name: 'commissions_displayNames', type: 'string[]', facet: true, optional: true },
    ],
    default_sorting_field: 'creation_time',
  },

  opportunities: {
    name: 'opportunities',
    fields: [
      { name: 'id', type: 'string', facet: false },
      { name: 'title', type: 'string', facet: false },
      { name: 'customerName', type: 'string', facet: false, optional: true },
      { name: 'customerId', type: 'string', facet: false, optional: true },
      { name: 'stage', type: 'string', facet: true, optional: true },
      { name: 'value', type: 'float', facet: false, optional: true },
      { name: 'probability', type: 'int32', facet: true, optional: true },
      { name: 'expectedCloseDate', type: 'string', facet: false, optional: true },
      { name: 'salesRepNames', type: 'string[]', facet: true, optional: true },
      { name: 'status', type: 'string', facet: true, optional: true },
      { name: 'createdAt', type: 'int64', facet: false }, // Unix timestamp - REQUIRED
      { name: 'updatedAt', type: 'int64', facet: false, optional: true },
    ],
    default_sorting_field: 'createdAt',
  },

  contacts: {
    name: 'contacts',
    fields: [
      { name: 'id', type: 'string', facet: false },
      { name: 'firstName', type: 'string', facet: false, optional: true, sort: true },
      { name: 'lastName', type: 'string', facet: false, optional: true, sort: true },
      { name: 'email', type: 'string', facet: false, optional: true },
      { name: 'phone', type: 'string', facet: false, optional: true },
      { name: 'customerName', type: 'string', facet: false, optional: true },
      { name: 'customerId', type: 'string', facet: false, optional: true },
      { name: 'customerAccountNumber', type: 'string', facet: false, optional: true },
      { name: 'title', type: 'string', facet: false, optional: true },
      { name: 'contactFocus', type: 'string[]', facet: true, optional: true },
      { name: 'hasContactFocus', type: 'bool', facet: true, optional: true },
      { name: 'salesRepNames', type: 'string[]', facet: true, optional: true },
      { name: 'isPrimary', type: 'bool', facet: true, optional: true },
      { name: 'status', type: 'string', facet: true, optional: true },
      { name: 'createdAt', type: 'int64', facet: false }, // Unix timestamp - REQUIRED
    ],
    default_sorting_field: 'createdAt',
  },

  users: {
    name: 'users',
    fields: [
      { name: 'id', type: 'string', facet: false },
      { name: 'displayName', type: 'string', facet: false, optional: true, sort: true },
      { name: 'firstName', type: 'string', facet: false, optional: true },
      { name: 'lastName', type: 'string', facet: false, optional: true },
      { name: 'email', type: 'string', facet: false, optional: true },
      { name: 'role', type: 'string', facet: true, optional: true },
      { name: 'department', type: 'string', facet: true, optional: true },
      { name: 'status', type: 'string', facet: true, optional: true },
      { name: 'createdAt', type: 'int64', facet: false }, // Unix timestamp - REQUIRED
    ],
    default_sorting_field: 'createdAt',
  },
};

/**
 * Create or update a collection schema
 */
export const ensureCollection = async (collectionName) => {
  try {
    const schema = COLLECTION_SCHEMAS[collectionName];
    if (!schema) throw new Error(`Unknown collection: ${collectionName}`);

    // Try to retrieve existing collection
    try {
      const existing = await typesenseRequest(`/collections/${collectionName}`);
      console.log(`[Typesense] Collection '${collectionName}' already exists`);
      return existing;
    } catch (err) {
      // Collection doesn't exist, create it
      console.log(`[Typesense] Creating collection '${collectionName}'...`);
      const result = await typesenseRequest('/collections', 'POST', schema);
      console.log(`[Typesense] Collection '${collectionName}' created successfully`);
      return result;
    }
  } catch (error) {
    console.error(`[Typesense] Error ensuring collection '${collectionName}':`, error);
    throw error;
  }
};

/**
 * Index a single document
 */
export const indexDocument = async (collectionName, document) => {
  try {
    await typesenseRequest(
      `/collections/${collectionName}/documents?action=upsert`,
      'POST',
      document
    );
    return true;
  } catch (error) {
    console.error(`[Typesense] Error indexing document to '${collectionName}':`, error);
    return false;
  }
};

/**
 * Partially update a document — only the provided fields are changed.
 * Uses action=emplace: creates the doc if it doesn't exist, otherwise merges.
 * Prefer this over indexDocument when you only know a subset of a document's fields,
 * to avoid accidentally wiping fields you didn't include.
 */
export const partialUpdateDocument = async (collectionName, document) => {
  try {
    await typesenseRequest(
      `/collections/${collectionName}/documents?action=emplace`,
      'POST',
      document
    );
    return true;
  } catch (error) {
    console.error(`[Typesense] Error partially updating document in '${collectionName}':`, error);
    return false;
  }
};

/**
 * Delete a document
 */
export const deleteDocument = async (collectionName, documentId) => {
  try {
    await typesenseRequest(
      `/collections/${collectionName}/documents/${documentId}`,
      'DELETE'
    );
    return true;
  } catch (error) {
    console.error(`[Typesense] Error deleting document from '${collectionName}':`, error);
    return false;
  }
};
/**
 * Perform a search
 * 
 * @param {string} collectionName - Collection to search
 * @param {object} options - Search options (q, query_by, filter_by, sort_by, per_page, etc.)
 * @returns {Promise<object>} - Search results
 */
export const search = async (collectionName, options = {}) => {
  try {
    const searchParams = {
      q: options.q || '*',
      query_by: options.query_by || getDefaultQueryFields(collectionName),
      filter_by: options.filter_by || '',
      sort_by: options.sort_by || '',
      per_page: options.per_page || 50,
      page: options.page || 1,
      facet_by: options.facet_by || '',
      max_facet_values: options.max_facet_values || 100,
    };

    // Remove empty parameters
    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] === '' || searchParams[key] === undefined) {
        delete searchParams[key];
      }
    });

    // Build query string
    const queryString = new URLSearchParams(searchParams).toString();
    const results = await typesenseRequest(`/collections/${collectionName}/documents/search?${queryString}`);
    
    return results;
  } catch (error) {
    console.error(`[Typesense] Search error in '${collectionName}':`, error);
    throw error;
  }
};

/**
 * Get default search fields for a collection
 */
const getDefaultQueryFields = (collectionName) => {
  const defaults = {
    customers: 'customerName,customerEmail,apparelMagicId,city,state',
    orders: 'order_number,customer_name,customer_id',
    opportunities: 'title,customerName',
    contacts: 'firstName,lastName,email,phone,customerName,customerAccountNumber,salesRepNames,title,contactFocus',
    users: 'displayName,firstName,lastName,email',
  };
  return defaults[collectionName] || 'id';
};

/**
 * Multi-collection search (search across multiple collections at once)
 */
export const multiSearch = async (searches) => {
  try {
    const searchRequests = searches.map(({ collection, options = {} }) => ({
      collection,
      q: options.q || '*',
      query_by: options.query_by || getDefaultQueryFields(collection),
      per_page: options.per_page || 50,
      ...options,
    }));

    const results = await typesenseRequest('/multi_search', 'POST', { searches: searchRequests });
    return results;
  } catch (error) {
    console.error('[Typesense] Multi-search error:', error);
    throw error;
  }
};

export default {
  isTypesenseAvailable,
  ensureCollection,
  indexDocument,
  deleteDocument,
  search,
  multiSearch,
  COLLECTION_SCHEMAS,
};


